import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useTitle } from "@/lib/img";
import { MaskedLines, FadeUp } from "@/components/Reveal";

const PROJECT_TYPES = ["Residential", "Hospitality", "Workplace", "Media Inquiry", "Other"];

const NDA_OPTIONS = ["Yes", "No"];

const INITIAL = { name: "", email: "", phone: "", project_type: "", location: "", nda: "", message: "" };

export default function Inquiries() {
  useTitle(
    "Inquiries | Medium Plenty",
    "Start a project with Medium Plenty — high-end residential, boutique hospitality, and workplace architecture and interiors. California-based, working worldwide."
  );
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/inquiries", form);
      setSent(true);
      toast.success("Inquiry received — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="inquiries-page" className="pt-32 md:pt-44 pb-24 md:pb-36 px-6 md:px-12 min-h-screen">
      <div className="grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <h1 className="font-serif font-light text-[9vw] md:text-[4vw] leading-[0.95] tracking-tight mb-10">
            <MaskedLines lines={["Inquiries"]} delay={0.15} />
          </h1>
          <FadeUp delay={0.2}>
            <p className="font-serif font-light text-sm md:text-base leading-[1.4] tracking-tight max-w-md">
              “Trust is the conduit for influence; it’s the medium through which ideas travel.”
            </p>
            <p className="kicker mt-5">— Amy Cuddy</p>
          </FadeUp>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          {sent ? (
            <div data-testid="inquiry-success" className="border border-line p-10 md:p-14">
              <p className="font-serif font-light text-4xl md:text-5xl tracking-tight mb-6">Thank you.</p>
              <p className="text-smoke leading-relaxed mb-10">
                Your inquiry has arrived safely. We read every note personally and will reply shortly. In the
                meantime, feel free to browse the portfolio.
              </p>
              <Link
                to="/projects"
                data-testid="inquiry-portfolio-link"
                className="group inline-flex items-center gap-2 editorial-link text-sm tracking-[0.08em]"
              >
                Portfolio
                <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1.5" />
              </Link>
            </div>
          ) : (
            <FadeUp delay={0.15}>
              <form data-testid="inquiry-form" onSubmit={submit} className="space-y-9">
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-9">
                  <div>
                    <label htmlFor="inq-name" className="field-label">Name *</label>
                    <input id="inq-name" data-testid="inquiry-name-input" required value={form.name} onChange={set("name")} className="field-input" placeholder="Your full name" />
                  </div>
                  <div>
                    <label htmlFor="inq-email" className="field-label">Email *</label>
                    <input id="inq-email" data-testid="inquiry-email-input" required type="email" value={form.email} onChange={set("email")} className="field-input" placeholder="you@example.com" />
                  </div>
                  <div>
                    <label htmlFor="inq-phone" className="field-label">Phone</label>
                    <input id="inq-phone" data-testid="inquiry-phone-input" value={form.phone} onChange={set("phone")} className="field-input" placeholder="Optional" />
                  </div>
                  <div>
                    <label htmlFor="inq-location" className="field-label">Project Location</label>
                    <input id="inq-location" data-testid="inquiry-location-input" value={form.location} onChange={set("location")} className="field-input" placeholder="City, State / Country" />
                  </div>
                  <div>
                    <label htmlFor="inq-type" className="field-label">Project Type</label>
                    <select id="inq-type" data-testid="inquiry-type-select" value={form.project_type} onChange={set("project_type")} className="field-input bg-paper">
                      <option value="">Select a type</option>
                      {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="inq-nda" className="field-label">NDA Required?</label>
                    <select id="inq-nda" data-testid="inquiry-nda-select" value={form.nda} onChange={set("nda")} className="field-input bg-paper">
                      <option value="">Select</option>
                      {NDA_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="inq-message" className="field-label">About the Project</label>
                  <textarea
                    id="inq-message"
                    data-testid="inquiry-message-input"
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    className="field-input resize-none"
                    placeholder="Site, timeline, aspirations — anything that helps us understand your vision."
                  />
                </div>
                <button
                  data-testid="inquiry-submit-button"
                  type="submit"
                  disabled={sending}
                  className="group inline-flex items-center gap-3 border border-ink px-10 py-4 text-sm tracking-[0.08em] hover:bg-ink hover:text-paper transition-colors duration-400 disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send Inquiry"}
                  <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                </button>
              </form>
            </FadeUp>
          )}
        </div>
      </div>
    </div>
  );
}
